// T220 · v2 Button — ground truth styles-v2.css §6.
//
// Pill (999px), 600 weight, 180ms background/transform/shadow. Four variants and
// three heights; every one of them clears the I32 44px touch floor (48 base /
// 44 sm / 52 lg), which `components.test.tsx` asserts per combination rather
// than on a sample — describe "Button — 4 variants × 3 sizes (12/12)" walks all
// twelve, and a second case asserts the 44px floor per size. (Pointer corrected
// 2026-09-16, T293: there is no `Button.test.tsx`.) The press feedback (`:active` scale .98) and the focus ring live in
// the index.css v2 layer, NOT here, so `prefers-reduced-motion` can cancel the
// scale in one place and no variant can be shipped without either (I24).
import type { ComponentPropsWithoutRef, ElementType } from 'react';
import { cx } from '../cx';

export type V2ButtonVariant = 'primary' | 'secondary' | 'on-dark' | 'outline-on-dark';
export type V2ButtonSize = 'md' | 'sm' | 'lg';

/** min-height per size, in px — the I32 floor is 44 and this is the proof surface. */
export const V2_BUTTON_MIN_HEIGHT: Record<V2ButtonSize, number> = { md: 48, sm: 44, lg: 52 };

// [T223] THE BORDER LIVES IN THE VARIANT, NEVER IN THE BASE.
// It used to be `border-2 border-transparent` here, with `secondary` writing
// `border-[1.5px] border-teal-700` after it. Those are same-specificity Tailwind
// utilities, so which one wins is decided by their order in the GENERATED
// stylesheet, not by the order they appear in the class attribute — and Tailwind
// emits `border-transparent` last. Measured in Chromium on the built bundle:
// `borderColor: rgba(0, 0, 0, 0)`. The secondary button has been shipping with
// NO visible outline since U1 — invisible to markup tests (the class string is
// right there), invisible to tsc, invisible to the build. Declaring width and
// colour once per variant removes the conflict instead of trying to win it —
// the same lesson the `v2-min40` band recorded for the FeeTable separators.
const BASE =
  'v2-btn inline-flex items-center justify-center gap-2 rounded-v2-pill border-solid ' +
  'font-semibold leading-[1.25] no-underline cursor-pointer ' +
  'transition-[background-color,transform,box-shadow] duration-[180ms] ease-[ease] ' +
  'hover:-translate-y-px ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none ' +
  'aria-disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:transform-none';

const VARIANTS: Record<V2ButtonVariant, string> = {
  primary: 'v2-btn-primary border-2 border-transparent bg-teal-700 text-white hover:bg-teal-600 hover:text-white',
  secondary:
    'v2-btn-secondary border-[1.5px] border-teal-700 bg-white text-teal-700 hover:bg-teal-50 hover:text-teal-800',
  // `on-dark` pairs teal-500 with a near-black ink from the ground truth (#04211e)
  // — white on teal-500 would not clear the contrast bar over imagery.
  'on-dark': 'v2-btn-on-dark border-2 border-transparent bg-teal-500 text-[#04211e] hover:bg-[#2cc6b0] hover:text-[#04211e]',
  'outline-on-dark':
    'v2-btn-outline-on-dark border-2 border-white/45 bg-transparent text-white hover:border-white hover:bg-white/[0.08] hover:text-white',
};

const SIZES: Record<V2ButtonSize, string> = {
  md: 'min-h-[48px] px-4 py-[0.45rem] text-base',
  sm: 'min-h-[44px] px-4 py-[0.45rem] text-[0.85rem]',
  lg: 'min-h-[52px] px-[2.2rem] py-[0.95rem] text-[1.02rem]',
};

export function v2ButtonClassName(
  variant: V2ButtonVariant = 'primary',
  size: V2ButtonSize = 'md',
  opts: { block?: boolean; elevated?: boolean; className?: string } = {},
): string {
  return cx(
    BASE,
    VARIANTS[variant],
    SIZES[size],
    opts.block && 'w-full',
    // the hero / CTA-band lift (`--shadow-cta`); off by default so a page never
    // gets a second elevated CTA by accident (I29: one solid primary per screen)
    opts.elevated && 'shadow-v2-cta',
    opts.className,
  );
}

type V2ButtonOwnProps<T extends ElementType> = {
  as?: T;
  variant?: V2ButtonVariant;
  size?: V2ButtonSize;
  /** full-width (`.btn-block`) */
  block?: boolean;
  /** the CTA drop shadow — hero + CTA band only */
  elevated?: boolean;
  className?: string;
};

export type V2ButtonProps<T extends ElementType = 'button'> = V2ButtonOwnProps<T> &
  Omit<ComponentPropsWithoutRef<T>, keyof V2ButtonOwnProps<T>>;

export function Button<T extends ElementType = 'button'>({
  as,
  variant = 'primary',
  size = 'md',
  block,
  elevated,
  className,
  ...rest
}: V2ButtonProps<T>) {
  const Comp = (as || 'button') as ElementType;
  return <Comp className={v2ButtonClassName(variant, size, { block, elevated, className })} {...rest} />;
}
